import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Loader2, Mail, Smartphone } from 'lucide-react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { businessApi } from '@/lib/api';

interface NotificationPrefs {
  notify_email_payment_received: boolean;
  notify_push_payment_received: boolean;
  notify_email_quote_approved: boolean;
  notify_push_quote_approved: boolean;
  notify_email_quote_changes_requested: boolean;
  notify_push_quote_changes_requested: boolean;
  notify_email_ach_payment_failed: boolean;
  notify_push_ach_payment_failed: boolean;
  notify_email_bounced_delivery: boolean;
  notify_push_bounced_delivery: boolean;
  notify_email_refund_completed: boolean;
  notify_push_refund_completed: boolean;
  notify_email_refund_failed: boolean;
  notify_push_refund_failed: boolean;
}

interface NotificationEvent {
  key: string;
  label: string;
}

const CATEGORIES: { title: string; events: NotificationEvent[] }[] = [
  {
    title: 'Payments',
    events: [
      { key: 'payment_received', label: 'Payment Received' },
      { key: 'ach_payment_failed', label: 'ACH Payment Failed' },
      { key: 'refund_completed', label: 'Refund Completed' },
      { key: 'refund_failed', label: 'Refund Failed' },
    ],
  },
  {
    title: 'Quotes',
    events: [
      { key: 'quote_approved', label: 'Quote Approved' },
      { key: 'quote_changes_requested', label: 'Changes Requested' },
    ],
  },
  {
    title: 'Delivery',
    events: [
      { key: 'bounced_delivery', label: 'Bounced Delivery' },
    ],
  },
];

export function NotificationSettings() {
  const { nav } = useBusinessNav();
  const { currentBusiness, refreshUser } = useApp();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (currentBusiness) {
      setPrefs({
        notify_email_payment_received: currentBusiness.notify_email_payment_received,
        notify_push_payment_received: currentBusiness.notify_push_payment_received,
        notify_email_quote_approved: currentBusiness.notify_email_quote_approved,
        notify_push_quote_approved: currentBusiness.notify_push_quote_approved,
        notify_email_quote_changes_requested: currentBusiness.notify_email_quote_changes_requested,
        notify_push_quote_changes_requested: currentBusiness.notify_push_quote_changes_requested,
        notify_email_ach_payment_failed: currentBusiness.notify_email_ach_payment_failed,
        notify_push_ach_payment_failed: currentBusiness.notify_push_ach_payment_failed,
        notify_email_bounced_delivery: currentBusiness.notify_email_bounced_delivery,
        notify_push_bounced_delivery: currentBusiness.notify_push_bounced_delivery,
        notify_email_refund_completed: currentBusiness.notify_email_refund_completed,
        notify_push_refund_completed: currentBusiness.notify_push_refund_completed,
        notify_email_refund_failed: currentBusiness.notify_email_refund_failed,
        notify_push_refund_failed: currentBusiness.notify_push_refund_failed,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness?.id]);

  const savePrefs = useCallback(async (prefsToSave: NotificationPrefs) => {
    if (!currentBusiness) return;
    const response = await businessApi.update(currentBusiness.id, prefsToSave);
    if (response.data) {
      await refreshUser();
    }
  }, [currentBusiness, refreshUser]);

  const update = useCallback(<K extends keyof NotificationPrefs>(key: K, value: NotificationPrefs[K]) => {
    setPrefs(prev => {
      if (!prev) return null;
      const newPrefs = { ...prev, [key]: value };

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        savePrefs(newPrefs);
      }, 500);

      return newPrefs;
    });
  }, [savePrefs]);

  if (!prefs) {
    return (
      <div className="px-6 pt-6 pb-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="px-6 pt-6 pb-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-3 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Notification Preferences</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how you want to be notified about important events.
        </p>
      </div>

      {CATEGORIES.map((category) => (
        <Card key={category.title} className="mb-4 rounded-2xl">
          <CardHeader className="pb-0">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {category.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            {/* Column headers */}
            <div className="flex items-center justify-end gap-6 mb-2 pr-1">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Mail className="h-3.5 w-3.5" />
                <span>Email</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Smartphone className="h-3.5 w-3.5" />
                <span>Push</span>
              </div>
            </div>

            <div className="space-y-1 rounded-lg border divide-y">
              {category.events.map((event) => {
                const emailKey = `notify_email_${event.key}` as keyof NotificationPrefs;
                const pushKey = `notify_push_${event.key}` as keyof NotificationPrefs;

                return (
                  <div key={event.key} className="flex items-center justify-between p-4">
                    <span className="text-sm font-medium">{event.label}</span>
                    <div className="flex items-center gap-6">
                      <Switch
                        checked={prefs[emailKey]}
                        onCheckedChange={(checked) => update(emailKey, checked)}
                      />
                      <Switch
                        checked={prefs[pushKey]}
                        onCheckedChange={(checked) => update(pushKey, checked)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      <p className="text-xs text-muted-foreground mt-2">
        In-app notifications always appear in your notification center.
      </p>
    </div>
  );
}
